import type React from "react";

import * as AccordionPrimitive from "@radix-ui/react-accordion";

import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/ui";

// Thin chevron matching production HSA PDP (14x9, navy fill); inherits color from `text-primary`
// so it themes per site (blue on HSA/FSA, purple on WDH).
const PDPAccordionChevron = ({ className }: { className?: string }) => (
  <svg
    aria-hidden
    viewBox="0 0 15 9"
    width="14"
    height="9"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    className={cn(
      "shrink-0 -translate-y-[4px] text-primary transition-transform duration-200",
      className,
    )}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.8958 0.408C13.3228 -0.136 12.3937 -0.136 11.8227 0.408L7.16275 4.831L2.50175 0.408C1.92975 -0.136 1.00175 -0.136 0.42975 0.408C-0.14325 0.952 -0.14325 1.834 0.42975 2.377L7.16475 8.764L13.8947 2.377C14.4677 1.834 14.4677 0.952 13.8947 0.408"
    />
  </svg>
);

const PDPAccordion = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) => (
  <AccordionPrimitive.Root
    ref={ref}
    className={cn("pdp-accordion", "w-full", className)}
    {...props}
  >
    {children}
  </AccordionPrimitive.Root>
);
PDPAccordion.displayName = "PDPAccordion";

const PDPAccordionItem = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn("pdp-accordion", "border-b border-gray-02", className)}
    {...props}
  >
    {children}
  </AccordionPrimitive.Item>
);
PDPAccordionItem.displayName = "PDPAccordionItem";

const PDPAccordionTrigger = ({
  children,
  className,
  ref,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) => (
  <AccordionPrimitive.Header className="pdp-accordion flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex w-full flex-1 items-center justify-between px-0 pb-[9px] pl-[4.5px] pt-[21px] text-left font-[family-name:'Grotesque',sans-serif] text-base font-extrabold leading-[1.44] tracking-[0.00625rem] text-primary lg:pb-[18px] lg:pl-[10px] lg:pt-[30px] lg:text-lg [&:focus-visible]:outline-none [&[data-state=open]>svg]:rotate-180",
        className,
      )}
      {...props}
    >
      {children}
      <PDPAccordionChevron className="mr-[5px] lg:mr-[10px]" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
);
PDPAccordionTrigger.displayName = "PDPAccordionTrigger";

const PDPAccordionContent = ({
  children,
  className,
  ref,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      "pdp-accordion",
      "overflow-hidden font-[family-name:'Grotesque',sans-serif] text-sm font-light leading-[1.57] text-foreground",
      className,
    )}
    {...props}
  >
    {/* -mt-1 mirrors production .description-and-detail .content { margin-top: -0.25rem } */}
    <div className="-mt-1 pb-[25px] pl-[4.5px] pt-0 lg:max-w-[80%] lg:pl-[10px]">
      {children}
    </div>
  </AccordionPrimitive.Content>
);
PDPAccordionContent.displayName = "PDPAccordionContent";

/**
 * Skeleton for the PDP info accordions section (description, details,
 * ingredients, etc.). Mirrors trigger row height + bottom-border styling
 * so the section doesn't jump when the awaited product resolves.
 */
const PDPAccordionsSkeleton = ({ rows = 5 }: { rows?: number } = {}) => (
  <section
    className="pdp-accordion mt-8 w-full px-5 sm:px-0"
    aria-label="Product info loading"
  >
    {Array.from({ length: rows }).map((_, i) => (
      <div
        key={i}
        className="flex items-center justify-between border-b border-gray-02 pb-[9px] pl-[4.5px] pt-[21px] lg:pb-[18px] lg:pl-[10px] lg:pt-[30px]"
      >
        <Skeleton className="h-5 w-48 lg:h-6 lg:w-56" />
        <Skeleton className="mr-[5px] h-2 w-3.5 lg:mr-[10px]" />
      </div>
    ))}
  </section>
);
PDPAccordionsSkeleton.displayName = "PDPAccordionsSkeleton";

export {
  PDPAccordion,
  PDPAccordionContent,
  PDPAccordionItem,
  PDPAccordionsSkeleton,
  PDPAccordionTrigger,
};
